import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { formatDate } from '@/lib/organization';

export interface ActivityEntry {
  /** The audit entry's own id, where there is one. Two can share a moment. */
  id?: string;
  /** When it happened, as the backend writes dates. */
  at: string;
  /** What happened, in a few words. */
  title: string;
  /** Anything worth adding: a reason, the fields that changed. */
  detail?: ReactNode;
  /** Who did it. Left out while nothing can say. */
  actor?: string;
}

interface ActivityPanelProps {
  /** True when a CollapsibleCard already supplies the card and the heading. */
  embedded?: boolean;
  entries: ActivityEntry[];
  /** Shown under the list: the link to the whole log, where there is one. */
  footer?: ReactNode;
}

/**
 * What has happened to an account, newest first.
 *
 * It takes entries and nothing else, so where they come from can change without
 * the panel changing with it. The account page fills it from the account's
 * audit feed, with `actor` set; for an account older than the log, whose feed
 * is empty, it falls back to the dates the record carries, with no actor.
 *
 * `detail` is a node rather than a string for the same reason: one audit entry
 * can carry several changed fields, and a sentence cannot hold them.
 */
export function ActivityPanel({ entries, embedded, footer }: ActivityPanelProps) {
  const newestFirst = [...entries].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );

  return (
    <Paper
      variant={embedded ? 'elevation' : 'outlined'}
      elevation={0}
      sx={{ p: embedded ? 0 : 3, bgcolor: 'transparent' }}
    >
      {embedded ? null : (
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Recent activity
        </Typography>
      )}

      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {newestFirst.map((entry) => (
          <Box
            component="li"
            key={entry.id ?? `${entry.at}-${entry.title}`}
            sx={{
              py: 1.5,
              '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {entry.title}
            </Typography>
            <Typography variant="caption" component="p" color="text.secondary">
              {formatDate(entry.at)}
            </Typography>
            {entry.detail ? (
              <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                {entry.detail}
              </Typography>
            ) : null}
            {/* No "by" line at all while nothing can say who. An empty one
                would read as nobody rather than as not yet known. */}
            {entry.actor ? (
              <Typography variant="caption" component="p" color="text.secondary">
                by {entry.actor}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>

      {footer ? <Box sx={{ mt: 2 }}>{footer}</Box> : null}
    </Paper>
  );
}
